import { CheckCircle, AlertCircle, XCircle, FileText } from 'lucide-react'
import ViabilityScore from './ViabilityScore'
import ComponentsAnalysis from './ComponentsAnalysis'
import KeywordsTable from './KeywordsTable'
import CompetitorsTable from './CompetitorsTable'
import NichesDisplay from './NichesDisplay'
import AmazonProductsPanel from './AmazonProductsPanel'
import FacebookAdsPanel from './FacebookAdsPanel'
import GoogleMapsPanel from './GoogleMapsPanel'
import GoogleTrendsPanel from './GoogleTrendsPanel'
import PinterestTrendsPanel from './PinterestTrendsPanel'
import DropshippingPanel from './DropshippingPanel'
import PlatformCapabilities from './PlatformCapabilities'

export default function ResultsPanel({ results, onGenerateReport }) {
  const getStatusIcon = (status) => {
    switch(status) {
      case 'VIABLE':
        return <CheckCircle className="w-8 h-8 text-[#94a3b8]er" />
      case 'RIESGOSO':
        return <AlertCircle className="w-8 h-8 text-yellow-600" />
      case 'NO VIABLE':
        return <XCircle className="w-8 h-8 text-red-600" />
      default:
        return null
    }
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Capacidades Analíticas — clickable tiles scrolling to each section */}
      <PlatformCapabilities results={results} />

      {/* Header de resultados */}
      <div className="gradient-glow p-6 rounded-xl">
        <div className="flex items-start justify-between mb-4 gap-4">
          <div className="flex-1">
            <h2 className="text-2xl font-bold text-white mb-2">
              {results.query}
            </h2>
            <p className="text-[#94a3b8]">
              Análisis de {results.type === 'PRODUCT' ? 'Producto' : 'Servicio'}
            </p>
          </div>
          <div className="flex flex-col gap-3 md:flex-row md:items-center">
            <div className="flex items-center gap-3">
              {getStatusIcon(results.status)}
              <div className="text-right">
                <p className="text-sm text-[#94a3b8]">Estado</p>
                <p className={`text-lg font-bold ${
                  results.status === 'VIABLE' ? 'text-[#94a3b8]er' :
                  results.status === 'RIESGOSO' ? 'text-yellow-600' :
                  'text-red-600'
                }`}>
                  {results.status}
                </p>
              </div>
            </div>
            {onGenerateReport && (
              <button
                onClick={onGenerateReport}
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-slate-medium to-slate-dark rounded-lg text-[#94a3b8]est font-semibold hover:shadow-lg hover:shadow-slate-medium/50 transition-all"
              >
                <FileText className="w-5 h-5" />
                <span>Reporte</span>
              </button>
            )}
          </div>
        </div>
        <p className="text-[#94a3b8] italic">
          💡 {results.recommendation}
        </p>
      </div>

      {/* Score de viabilidad */}
      <ViabilityScore score={results.score} />

      {/* Google Trends */}
      {results.googleTrends && (
        <div id="section-trends">
          <GoogleTrendsPanel trendsData={results.googleTrends} />
        </div>
      )}

      {/* Pinterest Trends */}
      {results.pinterestTrends && (
        <div id="section-pinterest">
          <PinterestTrendsPanel pinterestData={results.pinterestTrends} />
        </div>
      )}

      {/* Componentes del análisis */}
      <ComponentsAnalysis components={results.components} />

      {/* Nichos (si es SERVICE) */}
      {results.niches && results.niches.length > 0 && (
        <NichesDisplay niches={results.niches} />
      )}

      {/* Amazon products (PRODUCT mode) */}
      {results.type === 'PRODUCT' && results.amazon && (
        <div id="section-amazon">
          <AmazonProductsPanel amazonData={results.amazon} />
        </div>
      )}

      {/* Facebook Ads Library */}
      {results.facebookAds && (
        <div id="section-facebook">
          <FacebookAdsPanel facebookData={results.facebookAds} />
        </div>
      )}

      {/* Google Maps (SERVICE mode) */}
      {results.type === 'SERVICE' && results.googleMaps && (
        <div id="section-maps">
          <GoogleMapsPanel googleMapsData={results.googleMaps} />
        </div>
      )}

      {/* Dropshipping panel (AliExpress) */}
      {results.type === 'PRODUCT' && (
        <div id="section-aliexpress">
          {results.dropshipping?.not_configured ? (
            <div className="gradient-glow p-6 rounded-xl card-hover flex items-center gap-4">
              <div className="p-3 bg-slate-darker border border-slate-dark/30 rounded-lg shrink-0">
                <svg className="w-6 h-6 text-slate-medium" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" /></svg>
              </div>
              <div>
                <p className="text-sm font-semibold text-white">Dropshipping &#8212; AliExpress</p>
                <p className="text-xs text-slate-light mt-0.5">API no configurada &#8212; Ve a <span className="text-slate-lighter font-medium">Ajustes &rarr; APIs</span> para conectar AliExpress y ver precios, proveedores y m&#225;rgenes reales.</p>
              </div>
            </div>
          ) : results.dropshipping ? (
            <DropshippingPanel dropshippingData={results.dropshipping} />
          ) : null}
        </div>
      )}

      {/* Keywords */}
      <KeywordsTable keywords={results.keywords} source={results.keywordsSource} />

      {/* Competidores — solo si hay datos reales */}
      {results.competitors?.length > 0 && (
        <CompetitorsTable competitors={results.competitors} />
      )}
    </div>
  )
}
